import { css } from "@emotion/react"
import styled from "@emotion/styled"
import Link from "components/link/Link"
import Image from "components/media/ImageCLD"
import Price, { cssPrice } from "components/price/Price"
import WishlistButton from "components/wishlist-button/WishlistButton"
import { useDictionary } from "contexts/Dictionary"
import { useEnv } from "contexts/Env"
import { useNavigation } from "contexts/Navigation"
import getMediaQuery from "css/breakpoints"
import { body200, body50, bold, legend100, legend80 } from "css/text"
import { useId } from "react"
import { storeCustomEvent, waTriggerEvent } from "../../analytics/DigitalDataLayer"

const Item = styled.div`
  position: relative;
  background: rgb(var(--light-grey));
  display: grid;
  grid-template: "item";
  width: 100%;

  .wishlist-button-root {
    grid-area: item;
    z-index: 1;
    width: 24px;
    height: 24px;
    margin-block: ${props => (props.icon === "close" ? "20px 0" : "3vw 0")};
    ${props => (props.icon === "close" ? "justify-content: flex-end;" : "justify-content: flex-start;")};
    ${props => (props.icon === "close" ? "margin-inline-end: 20px;" : "margin-inline-start: 3vw;")};
    ${props => (props.icon === "close" ? "place-self: start end;" : "")};
    display: flex;
    align-items: center;

    &:hover svg {
      ${props => (props.icon === "close" ? "fill: rgb(var(--light-black)) !important" : "")};
    }

    ${getMediaQuery("xl")} {
      margin-block: ${props => (props.icon === "close" ? "20px 0" : "2vw 0")};
      ${props => (props.icon === "close" ? "margin-inline-end: 20px;" : "margin-inline-start: 2vw;")};
    }
  }
`
const LinkBlock = styled.a`
  grid-area: item;
  display: flex;
  flex-direction: ${({ source }) => (source === "wishlist" ? "row-reverse" : "column")};
  justify-content: start;
  align-items: center;
  width: 100%;
  height: 100%;
  text-decoration: none;
  color: inherit;
  padding-block-end: ${({ hasCTA }) => (hasCTA ? "3.5rem" : "0")};

  ${getMediaQuery("m")} {
    padding-block-end: 0;
    flex-direction: row;
  }
`
const Info = styled.div`
  min-height: 100px;
  box-sizing: border-box;
  padding-inline: 3vw;
  width: 100%;
  margin-block-start: -20px;

  ${getMediaQuery("m")} {
    order: -1;
    width: 52%;
    min-height: 40%;
    padding-inline: 3vw 0;
    margin-block: unset;
  }

  ${getMediaQuery("xl")} {
    padding-inline: 2vw 0;
  }

  h2,
  p {
    html.prefers-contrast & {
      color: black !important;
      position: relative;

      ::after {
        background: white;
        content: "";
        height: calc(100% + 30px);
        left: 50%;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        width: calc(100% + 20px);
        z-index: -1;
      }
    }
  }

  .${cssPrice} {
    ${legend100}
    margin-block-start: 5px;

    button {
      padding: 0.15rem;
    }

    ${getMediaQuery("xxll")} {
      .panel-open & {
        ${legend80}
      }
    }
  }
`

const Title = styled.h2`
  ${props => props.size}
  ${bold}
  line-height: 1.1;
  margin-block-end: 10px;

  ${getMediaQuery("xxll")} {
    .panel-open & {
      ${legend100}
    }
  }
`

const Para = styled.p`
  ${legend100}

  ${getMediaQuery("xxll")} {
    .panel-open & {
      ${legend80}
    }
  }
`

const Figure = styled.figure`
  width: 100%;

  img {
    display: block;

    ${props =>
      props.source === "wishlist" &&
      `
      position: relative;
      left: -10%;
      transform: scale(1.1);
    `}

    ${getMediaQuery("m")} {
      width: 94%;

      ${props =>
        props.source === "wishlist" &&
        `
        position: relative;
        left: -20%;
        transform: scale(1);
      `}
    }
  }

  ${getMediaQuery("m")} {
    width: 48%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`
const CTA = styled.div`
  ${bold};
  position: absolute;
  padding-inline: 3vw 0;
  bottom: 3vw;

  ${getMediaQuery("xl")} {
    padding-inline: 2vw 0;
  }

  a {
    padding-inline: 0 !important;
    // link have to be on the left
  }
`
const WishlistCTA = styled.div`
  ${bold};
  margin-block-start: 5px;

  a {
    color: rgb(var(--green)) !important;
    padding-inline: 0 !important;
    margin-inline-start: calc(var(--gap) * -1);
    // link have to be on the left

    svg {
      fill: rgb(var(--green)) !important;
    }

    &:hover {
      color: rgb(var(--light-black)) !important;

      svg {
        fill: rgb(var(--light-black)) !important;
      }
    }

    html.prefers-contrast & {
      padding-inline-end: 10px !important;
    }
  }
`

const Labink = styled.div`
  ${props =>
    props.source === "wishlist"
      ? `
    position: absolute;
    bottom: 3vw;
    left: 3vw;
  `
      : `
    padding-inline: 3vw;
    padding-block: 0 3vw;
    width: 100%;
    box-sizing: border-box;
  `}

  ${getMediaQuery("m")} {
    position: absolute;
    bottom: 3vw;
    left: 3vw;
    margin: 0;
    padding: unset;
    width: unset;

    [dir="rtl"] & {
      left: unset;
      right: 3vw;
    }
  }

  html.prefers-contrast & {
    > div {
      position: relative;
      width: fit-content;
      > span {
        position: relative;
        z-index: 1;
        color: black !important;
      }

      ${props =>
        props.source !== "wishlist"
          ? `
            ::after {
              background: rgb(var(--pure-white));
              content: "";
              height: calc(30% + 30px);
              left: 50%;
              position: absolute;
              top: 50%;
              transform: translate(-50%, -50%);
              width: calc(100% + 20px);
              z-index: 0;

              ${getMediaQuery("m")} {
                height: calc(100% + 30px);
              }
            }`
          : ""}
    }
  }

  ${getMediaQuery("xl")} {
    bottom: 2vw;
    left: 2vw;

    [dir="rtl"] & {
      left: unset;
      right: 2vw;
    }
  }
`
export const LabelFont = css`
  ${legend100}
  ${bold};
  // color: rgb(var(--light-black));
  display: block;

  ${getMediaQuery("xxll")} {
    .panel-open & {
      ${legend80}
    }
  }
`
const Label = styled.span`
  ${LabelFont}
`

export default function WatchCard(props) {
  const id = useId()
  const env = useEnv()
  const { rmc, title, familyCode, facet_case_title, newmodelselection, icon, cta, source, sourceName, alt, formattedPrice, noPrice, pricePopin } = props

  const navigation = useNavigation()
  const dictionary = useDictionary()

  const href = familyCode ? `/watches/${familyCode}/${rmc}` : ""

  const image = {
    alt: alt,
    loading: "lazy",
    sizes: "(min-width: 80rem) 15vw, (min-width: 48rem) 25vw, 50vw",
    sources: [
      {
        public_id: `catalogue/${env.catalogYear}/upright-bba-with-shadow/${rmc}`,
        version: 1,
        height: "1000",
        width: "680",
        policy: "t_v7-grid",
      },
    ],
  }
  //
  const onCTAClick = e => {
    //console.log({ source })
    e.preventDefault()
    if (source === "ymal") {
      storeCustomEvent({ eventName: "ymalClicked" })
      const gcpr = global.coveoRecommendationEngine?.state?.productRecommendations
      if (gcpr) {
        const item = gcpr.recommendations.find(v => v.permanentid === rmc)
        const dp = gcpr.recommendations.indexOf(item)
        const ymalInfo = {
          documentPosition: `${dp + 1}`,
          searchQueryUid: gcpr.searchUid,
          sourceName,
        }
        localStorage?.setItem("ymalInfo", JSON.stringify(ymalInfo))
      }
    }
    navigation.navigate(e.currentTarget.getAttribute("href"))
  }

  const triggerDownloadEvent = () => {
    if (source === "wishlist") {
      waTriggerEvent({
        eventName: "download",
        eventType: "Click",
        products: [
          {
            productCategory: familyCode,
            productID: rmc,
            productQuantity: "1",
          },
        ],
        downloadInfo: {
          fileName: new URL(navigation.localize(`/watches/${familyCode}/${rmc}.pdf`), env.assets).href,
          type: "brochure",
          position: "wishlist page",
          size: "",
        },
      })
    }
  }

  const openBrochure = (e, url) => {
    e.preventDefault()
    e.stopPropagation()

    // open brochure in new tab
    window.open(url, "_blank")

    triggerDownloadEvent()
  }

  return (
    <Item icon={icon} key={id}>
      <LinkBlock
        onClick={onCTAClick}
        href={navigation.localize(href)}
        aria-label={`${title} - ${facet_case_title}`}
        hasCTA={!!(cta && (cta.href || cta.external))}
        source={source}
      >
        <Figure source={source}>
          <Image {...image} />
        </Figure>

        <Info>
          <Title size={source === "wishlist" ? body200 : body50}>{title}</Title>
          <Para>{facet_case_title}</Para>
          {source !== "ymal" && !noPrice && <Price rmc={rmc} formattedPrice={formattedPrice} pricePopin={pricePopin} />}
        </Info>

        <Labink source={source}>
          {newmodelselection && (
            <div>
              <Label>{dictionary.newModel()}</Label>
            </div>
          )}
          {cta && source === "wishlist" && (
            <WishlistCTA>
              <Link
                className='inline small'
                icon={cta.icon}
                href={cta.href}
                target={cta?.target || null}
                iconColor='rgb(var(--green))'
                external={cta.external}
                onClick={e => openBrochure(e, cta.external)}
              >
                {cta.text}
              </Link>
            </WishlistCTA>
          )}
        </Labink>
      </LinkBlock>

      <WishlistButton rmc={rmc} family={familyCode} modelName={title} icon={icon} />
      {cta && source !== "wishlist" && (
        <CTA>
          <Link
            className='inline small'
            icon={cta.icon}
            href={cta.href}
            target={cta?.target || null}
            iconColor='rgb(var(--green))'
            download={cta.download}
            external={cta.external}
            onClick={triggerDownloadEvent}
          >
            {cta.text}
          </Link>
        </CTA>
      )}
    </Item>
  )
}
